import { useCart, stepFor } from '../store/cart';
import { amount, quantity, unitLabel } from '../lib/format';
import { Icon } from './icons';
import { cx, Kbd } from './ui';
import { useT } from '../i18n';

/**
 * The confirmation strip under the scan bar.
 *
 * After every scan or tap the cashier glances here, not at the bill: the item's name in large
 * type, its price, what is left on the shelf, a stepper for "two of those", and Undo for the
 * wrong packet. Undo takes back exactly what that scan added (one piece, or one weighing), so a
 * cashier who scanned a third milk by mistake does not lose the first two.
 *
 * With nothing scanned yet it says the till is ready, at the same height, so the grid below
 * never jumps.
 */
export function JustAdded({ disabled = false, findKey = 'F2' }: { disabled?: boolean; findKey?: string }) {
  const t = useT();
  const lastAdded = useCart((state) => state.lastAdded);
  const line = useCart((state) => (state.lastAdded ? state.lines.find((entry) => entry.key === state.lastAdded!.key) : undefined));
  const increment = useCart((state) => state.increment);
  const undo = useCart((state) => state.undoLastAdd);

  if (!line || !lastAdded) {
    return (
      <div className="flex h-16 shrink-0 items-center gap-3 rounded-2xl border border-dashed short:h-11 border-slate-300 px-4 text-sm text-slate-500 max-md:hidden" data-testid="just-added-idle">
        <span aria-hidden className="flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400">
          <Icon name="check" size={18} strokeWidth={2.2} />
        </span>
        <span className="min-w-0 flex-1 truncate">
          {disabled ? t('added.idleNoShift') : t('added.idle')}
        </span>
        <span className="hidden items-center gap-1.5 text-xs lg:inline-flex">
          <Kbd>{findKey}</Kbd> {t('added.findsAnything')}
        </span>
      </div>
    );
  }

  const step = stepFor(line);
  const left = line.stockMilli - line.qtyMilli;
  const weighed = line.allowDecimalQty;

  return (
    <div
      className="flex h-16 shrink-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-3 sm:px-4 short:h-12"
      style={{ boxShadow: 'var(--ui-shadow-1)' }}
      data-testid="just-added"
      role="status"
      aria-live="polite"
    >
      <span
        aria-hidden
        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full short:h-8 short:w-8"
        style={{ background: 'var(--ui-success-soft)', color: 'var(--ui-success)' }}
      >
        <Icon name="check" size={20} strokeWidth={2.6} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="text-[10.5px] font-bold tracking-[0.08em] uppercase short:hidden" style={{ color: 'var(--ui-success)' }}>
          {weighed ? t('added.weighed', { qty: quantity(lastAdded.qtyMilli), unit: unitLabel(line.unit) }) : t('added.title')}
        </p>
        <p className="flex min-w-0 items-baseline gap-2">
          <span className="max-w-full shrink-0 truncate text-lg leading-tight font-bold text-slate-900 short:text-base" data-testid="just-added-name">
            {line.productName}
          </span>
          <span className="tabular hidden min-w-0 truncate text-sm text-slate-500 sm:inline">
            ₹{amount(line.ratePaise)}/{unitLabel(line.unit)}
            {/* An open item is not stock: there is no shelf to count. */}
            {` · ${line.openItem ? t('added.openItem') : left >= 0 ? t('added.left', { qty: quantity(left) }) : t('added.overShelf')}`}
          </span>
        </p>
      </div>
      <div className="stepper-sm h-10 shrink-0 short:h-9" role="group" aria-label={t('added.qtyOf', { name: line.productName })}>
        <button type="button" disabled={disabled || line.qtyMilli <= step} aria-label={t('added.oneLess', { name: line.productName })} onClick={() => increment(line.key, -step)}>
          −
        </button>
        <span className="tabular flex min-w-12 items-center justify-center border-x border-slate-200 px-2 text-base font-bold text-slate-900">
          {quantity(line.qtyMilli)}
        </span>
        <button type="button" disabled={disabled} aria-label={t('added.oneMore', { name: line.productName })} onClick={() => increment(line.key, step)}>
          +
        </button>
      </div>
      <button
        type="button"
        className={cx('btn btn-ghost h-10 shrink-0 px-3 short:h-9 short:min-h-9')}
        disabled={disabled}
        data-testid="just-added-undo"
        title={t('added.undoTitle')}
        onClick={undo}
      >
        <Icon name="undo" size={17} />
        <span className="hidden sm:inline">{t('added.undo')}</span>
      </button>
    </div>
  );
}
